/**
 * Pinned Messages Panel Component
 * Displays pinned messages in a collapsible panel at the top of the channel
 */

import { useState } from 'react';
import { usePinnedMessages, useUnpinMessage } from '@/hooks/usePinnedMessages';
import Avatar from '@/components/common/Avatar';

interface PinnedMessagesPanelProps {
  channelId: string;
}

const PinnedMessagesPanel = ({ channelId }: PinnedMessagesPanelProps) => {
  const { data: pinnedMessages, isLoading } = usePinnedMessages(channelId);
  const { mutate: unpinMessage, isPending } = useUnpinMessage();
  const [isExpanded, setIsExpanded] = useState(false);

  if (isLoading) {
    return null;
  }

  if (!pinnedMessages || pinnedMessages.length === 0) {
    return null;
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('bg-BG', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateContent = (content: string, maxLength: number = 80) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const handleUnpin = (e: React.MouseEvent, messageId: string) => {
    e.stopPropagation();
    unpinMessage({ channelId, messageId });
  };

  const handleScrollToMessage = (messageId: string) => {
    const targetElement = document.getElementById(`message-${messageId}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetElement.classList.add('bg-blue-100');
      setTimeout(() => {
        targetElement.classList.remove('bg-blue-100');
      }, 2000);
    }
  };

  return (
    <div className="border-b border-gray-200 bg-blue-50">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <svg
            className="w-5 h-5 text-blue-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          <span className="font-medium text-sm text-gray-900">
            Закачени съобщения ({pinnedMessages.length})
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isExpanded ? 'transform rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-2 max-h-96 overflow-y-auto">
          {pinnedMessages.map((pin) => (
            <div
              key={pin.id}
              onClick={() => handleScrollToMessage(pin.message_id)}
              className="bg-white rounded-lg p-3 border border-gray-200 hover:border-blue-300 transition-colors group cursor-pointer"
            >
              {/* Message Header */}
              <div className="flex items-start space-x-2">
                <Avatar user={pin.messages.users} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {pin.messages.users.display_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTime(pin.messages.created_at)}
                    </span>
                  </div>

                  {/* Message Content */}
                  <div className="mt-1 text-sm text-gray-700">
                    {truncateContent(pin.messages.content)}
                  </div>

                  {/* Pinned By */}
                  <div className="mt-2 text-xs text-gray-500">
                    Закачено от {pin.users.display_name} •{' '}
                    {formatTime(pin.pinned_at)}
                  </div>
                </div>

                {/* Unpin Button */}
                <button
                  onClick={(e) => handleUnpin(e, pin.message_id)}
                  disabled={isPending}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                  title="Откачи съобщение"
                >
                  <svg
                    className="w-4 h-4 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PinnedMessagesPanel;
